/**
 * useAccompagnementStore — état des écrans « Demande d'accompagnement ».
 *
 * Liste paginée « côté serveur » (mockée), recherche débouncée, annulation de la
 * requête en vol. Ne dépend QUE de la couche `accompagnementApi` (contrat).
 *
 * La liste est ADAPTATIVE selon le rôle via `view` :
 *   - ALL          : toute la file (dispatcher / admin technique)
 *   - MINE_CREATED : mes demandes (commercial)
 *   - TEAM         : mes demandes + celles de mon équipe (manager, récursif)
 *   - ASSIGNED_ME  : celles qui me sont attribuées (technicien)
 *   - UNASSIGNED   : non attribuées (dispatcher)
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { accompagnementApi } from '@/v2/accompagnement/api/accompagnementApi'
import type {
  AccompActionInput,
  AccompActor,
  AccompCreateInput,
  AccompListFilters,
  AccompOutcome,
  AccompRequest,
  AccompSortField,
  AccompSortOrder,
  AccompStats,
  AccompStatsPeriod,
  AccompStatus,
  AccompTechnician,
  AccompUpdateInput,
} from '@/v2/accompagnement/api/accompagnementTypes'

export type AccompView = 'ALL' | 'MINE_CREATED' | 'TEAM' | 'ASSIGNED_ME' | 'UNASSIGNED'
export interface AccompViewOption {
  value: AccompView
  label: string
}

const PAGE_SIZE = 20
const DEBOUNCE_MS = 350

export const useAccompagnementStore = defineStore('accompagnement', () => {
  const perms = useBoostCorePermissionStore()

  // — Identité de l'acteur (pour les mutations) —
  const actor = computed<AccompActor>(() => {
    const u = perms.user
    const name = u ? `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email : 'Utilisateur'
    return { uuid: u?.uuid ?? 'me', name }
  })
  const currentUserUuid = computed(() => perms.user?.uuid ?? 'me')

  // — Filtres liste —
  const q = ref('')
  const statusFilter = ref<AccompStatus[]>([])
  const view = ref<AccompView>('MINE_CREATED')
  const sort = ref<AccompSortField>('createdAt')
  const order = ref<AccompSortOrder>('desc')
  const page = ref(1)
  const pageSize = ref(PAGE_SIZE)

  // — Données liste —
  const rows = ref<AccompRequest[]>([])
  const total = ref(0)
  const loading = ref(false)
  const error = ref(false)

  // — Détail —
  const current = ref<AccompRequest | null>(null)
  const currentLoading = ref(false)
  const currentError = ref(false)

  // — Mutations —
  const actionLoading = ref(false)
  const actionError = ref<string | null>(null)

  // — Techniciens assignables —
  const technicians = ref<AccompTechnician[]>([])

  // — Stats —
  const stats = ref<AccompStats | null>(null)
  const statsLoading = ref(false)

  let abort: AbortController | null = null
  let debounceTimer: ReturnType<typeof setTimeout> | null = null

  const pageCount = computed(() => Math.max(1, Math.ceil(total.value / pageSize.value)))
  const from = computed(() => (total.value === 0 ? 0 : (page.value - 1) * pageSize.value + 1))
  const to = computed(() => Math.min(page.value * pageSize.value, total.value))

  // — Vues disponibles selon le rôle —
  const availableViews = computed<AccompViewOption[]>(() => {
    const list: AccompViewOption[] = []
    if (perms.can('ACCOMP_ADMIN')) list.push({ value: 'ALL', label: 'Toutes' })
    list.push({ value: 'MINE_CREATED', label: 'Mes demandes' })
    if (perms.isManager && perms.recursiveManagedUserUuids.length) {
      list.push({ value: 'TEAM', label: 'Mon équipe' })
    }
    if (perms.can('ACCOMP_WRITE')) list.push({ value: 'ASSIGNED_ME', label: 'Qui me sont attribuées' })
    if (perms.can('ACCOMP_ADMIN')) list.push({ value: 'UNASSIGNED', label: 'Non attribuées' })
    return list
  })

  function defaultView(): AccompView {
    if (perms.can('ACCOMP_ADMIN')) return 'ALL'
    if (perms.can('ACCOMP_WRITE')) return 'ASSIGNED_ME'
    // Manager (a une équipe) → « Mon équipe » par défaut ; bascule possible sur « Mes demandes ».
    if (perms.isManager && perms.recursiveManagedUserUuids.length) return 'TEAM'
    return 'MINE_CREATED'
  }

  function viewFilters(): Partial<AccompListFilters> {
    switch (view.value) {
      case 'MINE_CREATED':
        return { createdByUuids: [currentUserUuid.value] }
      case 'TEAM':
        return { createdByUuids: [currentUserUuid.value, ...perms.recursiveManagedUserUuids] }
      case 'ASSIGNED_ME':
        return { assignedToUuid: currentUserUuid.value }
      case 'UNASSIGNED':
        return { unassignedOnly: true }
      case 'ALL':
      default:
        return {}
    }
  }

  async function fetchList() {
    abort?.abort()
    abort = new AbortController()
    loading.value = true
    error.value = false
    try {
      const res = await accompagnementApi.list({
        q: q.value.trim() || undefined,
        status: statusFilter.value.length ? statusFilter.value : undefined,
        ...viewFilters(),
        page: page.value,
        pageSize: pageSize.value,
        sort: sort.value,
        order: order.value,
        signal: abort.signal,
      })
      rows.value = res.data
      total.value = res.total
      loading.value = false
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'AbortError') return
      error.value = true
      loading.value = false
    }
  }

  function reloadNow() {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
    void fetchList()
  }

  function setQuery(value: string) {
    q.value = value
    page.value = 1
    if (debounceTimer) clearTimeout(debounceTimer)
    loading.value = true
    debounceTimer = setTimeout(() => {
      debounceTimer = null
      void fetchList()
    }, DEBOUNCE_MS)
  }

  function setPage(p: number) {
    const next = Math.min(Math.max(1, p), pageCount.value)
    if (next === page.value) return
    page.value = next
    reloadNow()
  }

  function setSort(field: AccompSortField) {
    if (sort.value === field) {
      order.value = order.value === 'asc' ? 'desc' : 'asc'
    } else {
      sort.value = field
      order.value = 'desc'
    }
    page.value = 1
    reloadNow()
  }

  function toggleStatus(status: AccompStatus) {
    const set = new Set(statusFilter.value)
    if (set.has(status)) set.delete(status)
    else set.add(status)
    statusFilter.value = [...set]
    page.value = 1
    reloadNow()
  }

  function setView(v: AccompView) {
    if (view.value === v) return
    view.value = v
    page.value = 1
    reloadNow()
  }

  /** Entrée sur la liste : choisit la vue par défaut selon le rôle puis charge. */
  function initForRole() {
    q.value = ''
    statusFilter.value = []
    sort.value = 'createdAt'
    order.value = 'desc'
    page.value = 1
    view.value = defaultView()
    void fetchList()
  }

  async function fetchOne(id: string) {
    currentLoading.value = true
    currentError.value = false
    current.value = null
    try {
      current.value = await accompagnementApi.get(id)
    } catch {
      currentError.value = true
    } finally {
      currentLoading.value = false
    }
  }

  /** Applique le résultat d'une mutation au détail courant + à la ligne de liste. */
  function applyUpdated(updated: AccompRequest) {
    if (current.value?.id === updated.id) current.value = updated
    const idx = rows.value.findIndex((r) => r.id === updated.id)
    if (idx !== -1) rows.value[idx] = updated
  }

  async function runMutation<T>(fn: () => Promise<T>): Promise<T | null> {
    actionLoading.value = true
    actionError.value = null
    try {
      return await fn()
    } catch (e: unknown) {
      actionError.value = e instanceof Error ? e.message : 'Une erreur est survenue.'
      return null
    } finally {
      actionLoading.value = false
    }
  }

  async function create(input: AccompCreateInput) {
    return runMutation(() => accompagnementApi.create(input, actor.value))
  }

  async function update(id: string, patch: AccompUpdateInput) {
    const res = await runMutation(() => accompagnementApi.update(id, patch, actor.value))
    if (res) applyUpdated(res)
    return res
  }

  async function assign(id: string, technicianUuid: string, note?: string) {
    const res = await runMutation(() => accompagnementApi.assign(id, technicianUuid, actor.value, note))
    if (res) applyUpdated(res)
    return res
  }

  async function takeCharge(id: string) {
    const res = await runMutation(() => accompagnementApi.takeCharge(id, actor.value))
    if (res) applyUpdated(res)
    return res
  }

  async function refuse(id: string, note: string) {
    const res = await runMutation(() => accompagnementApi.refuse(id, note, actor.value))
    if (res) applyUpdated(res)
    return res
  }

  async function remind(id: string, note: string) {
    const res = await runMutation(() => accompagnementApi.remind(id, note, actor.value))
    if (res) applyUpdated(res)
    return res
  }

  /** Admin technique : s'attribuer ET démarrer (auto-acceptation) en une fois. */
  async function selfAssignAndStart(id: string) {
    return runMutation(async () => {
      await accompagnementApi.assign(id, currentUserUuid.value, actor.value)
      const res = await accompagnementApi.takeCharge(id, actor.value)
      applyUpdated(res)
      return res
    })
  }

  async function addComment(id: string, message: string) {
    const res = await runMutation(() => accompagnementApi.addComment(id, message, actor.value))
    if (res) applyUpdated(res)
    return res
  }

  async function addAction(id: string, input: AccompActionInput) {
    const res = await runMutation(() => accompagnementApi.addAction(id, input, actor.value))
    if (res) applyUpdated(res)
    return res
  }

  async function deleteAction(id: string, actionId: string) {
    const res = await runMutation(() => accompagnementApi.deleteAction(id, actionId))
    if (res) applyUpdated(res)
    return res
  }

  async function attachQuote(id: string, quoteUuid: string) {
    const res = await runMutation(() => accompagnementApi.attachQuote(id, quoteUuid))
    if (res) applyUpdated(res)
    return res
  }

  async function detachQuote(id: string, linkId: string) {
    const res = await runMutation(() => accompagnementApi.detachQuote(id, linkId))
    if (res) applyUpdated(res)
    return res
  }

  /** Devis candidats au rattachement (picker) — renvoie la liste, sans état partagé. */
  function fetchQuotePicks(id: string, from?: string, to?: string) {
    return accompagnementApi.listQuotes(id, from, to)
  }

  /** Toutes les demandes d'un client (fiche client) — renvoie la liste, sans état partagé. */
  async function fetchClientRequests(clientUuid: string) {
    return accompagnementApi.listByClient(clientUuid)
  }

  async function close(id: string, outcome: AccompOutcome, note?: string) {
    const res = await runMutation(() => accompagnementApi.close(id, outcome, actor.value, note))
    if (res) applyUpdated(res)
    return res
  }

  async function cancel(id: string, note?: string) {
    const res = await runMutation(() => accompagnementApi.cancel(id, actor.value, note))
    if (res) applyUpdated(res)
    return res
  }

  async function fetchTechnicians() {
    const list = await accompagnementApi.listTechnicians()
    // L'utilisateur courant est lui-même assignable (s'auto-attribuer / test du rôle technicien).
    const me: AccompTechnician = { uuid: currentUserUuid.value, name: `${actor.value.name} (moi)`, openCount: 0 }
    technicians.value = [me, ...list.filter((t) => t.uuid !== me.uuid)]
  }

  async function fetchStats(period: AccompStatsPeriod) {
    statsLoading.value = true
    try {
      stats.value = await accompagnementApi.stats(period)
    } finally {
      statsLoading.value = false
    }
  }

  return {
    // identité
    actor,
    currentUserUuid,
    // filtres
    q,
    statusFilter,
    view,
    sort,
    order,
    page,
    pageSize,
    // liste
    rows,
    total,
    loading,
    error,
    pageCount,
    from,
    to,
    availableViews,
    fetchList,
    reload: reloadNow,
    setQuery,
    setPage,
    setSort,
    toggleStatus,
    setView,
    initForRole,
    // détail
    current,
    currentLoading,
    currentError,
    fetchOne,
    // mutations
    actionLoading,
    actionError,
    create,
    update,
    assign,
    takeCharge,
    refuse,
    remind,
    selfAssignAndStart,
    addComment,
    addAction,
    deleteAction,
    attachQuote,
    detachQuote,
    fetchQuotePicks,
    fetchClientRequests,
    close,
    cancel,
    // techniciens
    technicians,
    fetchTechnicians,
    // stats
    stats,
    statsLoading,
    fetchStats,
  }
})
